import { Badge, Brand, Button } from '@farol-ds/react'
import {
  HaystackItem,
  HaystackResponse,
  JurisprudenceMetadata,
} from '@jusbrasil-web/shared-apollo-client/types'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import { GA4ClickEvent } from '@jusbrasil-web/shared-ga4-events'

import { ExploreWithJusButton, StaticChatMessagesProps } from '../../components'
import { TesesJuridicasChatPageProps } from './teses-juridicas-chat'

const QUESTION_TEMPLATE =
  'No acervo de julgados do Jusbrasil existem precedentes que defendem a tese de que {TESE}?'

const getCleanThesisClusterLabel = (clusterLabel: string) =>
  clusterLabel.charAt(0).toLowerCase() + clusterLabel.slice(1).replace(/\.$/, '')

export const getUserQuestion = (clusterLabel: string) => {
  const cleanThesisClusterLabel = getCleanThesisClusterLabel(clusterLabel)
  return QUESTION_TEMPLATE.replace('{TESE}', cleanThesisClusterLabel)
}

export const getURLChatJusia = (question: string) => {
  const URL_CHAT_JUSIA = 'https://ia.jusbrasil.com.br/conversa'
  return `${URL_CHAT_JUSIA}?q=${encodeURIComponent(question)}&send`
}

const AssistantAnswer = ({
  cleanThesisClusterLabel,
  hits,
  documents,
  onCTAClick,
  userQuestion,
  clusterId,
  clusterLabel,
}: {
  clusterLabel: string
  cleanThesisClusterLabel: string
  hits: number
  documents: HaystackItem[]
  onCTAClick: () => void
  userQuestion: string
  clusterId: string
}) => {
  const { sendGA4Event } = useGA4Dispatcher()

  const onDocViewClick = (data: JurisprudenceMetadata) => {
    sendGA4Event(
      new GA4ClickEvent({
        componentName: 'cluster_theses_jurisprudence',
        componentClass: 'result_snippet',
        selectedOption: 'static_reference',
        resourceId: `JURISPRUDENCIA:${data.docId}`,
      })
    )
  }

  return (
    <article>
      <p>
        Sim, no acervo do Jusbrasil existem precedentes que corroboram a tese de que{' '}
        {cleanThesisClusterLabel}.
      </p>
      <p>
        <ExploreWithJusButton title={clusterLabel} clusterId={clusterId} />
      </p>
      <p>
        O Jusbrasil processou um grande volume de casos e selecionou <b>{hits} julgados</b> que se
        alinham a essa interpretação. A seguir, vamos abordar alguns julgados que corroboram essa
        tese.
      </p>
      <h2>Jurisprudência selecionada sobre essa tese:</h2>
      <ul>
        {documents.map((document, idx) => {
          const data = document.data as JurisprudenceMetadata
          const { court, date, url } = data
          return (
            <li key={idx}>
              <p>
                <b>
                  Caso julgado pelo {court.replace('_', '-')} em{' '}
                  {new Date(date ?? '').getFullYear()}:
                </b>{' '}
                {data.addons?.plainFacts ??
                  'Ainda não há um resumo dos fatos para este caso, mas ele é relevante porque compartilha a mesma linha argumentativa que fundamenta a tese.'}{' '}
                <a href={url} target="_blank" rel="noreferrer" onClick={() => onDocViewClick(data)}>
                  <Badge count={idx + 1} type="tertiary" />
                </a>
              </p>
            </li>
          )
        })}
      </ul>
      <p>
        <b>
          Quer verificar as ementas desses julgados na íntegra? Ou você pode contar mais detalhes
          sobre o seu caso específico e refinar essa pesquisa. Como gostaria de continuar?
        </b>
      </p>
      <Button leftIcon={<Brand onlySymbol width={20} height={20} />} onClick={onCTAClick} asChild>
        <a href={getURLChatJusia(userQuestion)}>Mostrar {hits} referências</a>
      </Button>
    </article>
  )
}

export const buildMessages = (
  props: Pick<TesesJuridicasChatPageProps, 'data' | 'clusterLabel' | 'clusterId'> & {
    onAnswerCTAClick: () => void
  }
): StaticChatMessagesProps['messages'] => {
  const { data, clusterLabel, clusterId, onAnswerCTAClick } = props
  const { root } = data
  const { searchHaystack } = root
  const { items, hits } = searchHaystack as HaystackResponse
  const documents = items as HaystackItem[]

  const cleanThesisClusterLabel = getCleanThesisClusterLabel(clusterLabel)
  const userQuestion = getUserQuestion(clusterLabel)

  return [
    {
      role: 'user',
      content: userQuestion,
    },
    {
      role: 'assistant',
      content: (
        <AssistantAnswer
          clusterLabel={clusterLabel}
          cleanThesisClusterLabel={cleanThesisClusterLabel}
          documents={documents}
          hits={hits}
          onCTAClick={onAnswerCTAClick}
          userQuestion={userQuestion}
          clusterId={clusterId}
        />
      ),
    },
  ]
}
